import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import routes from "../../../../routes/routes";
import { RootState } from "../../../../redux/store";
import {
    bookingStepChanged,
    bookingFlowChanged,
    setSelectedLocation as setSelectedLocationAction,
    setHasSelectedLocation as setHasSelectedLocationAction,
    setUseCase,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { setSelectedLocation as setSubscriptionSelectedLocation } from "../../../../redux/slice/subscription/subscriptionSlice";
import KeyHandoverNavigateNextAction from "../common/util/KeyHandoverNavigateNextAction";
import { useKeyHandoverLocations } from "../context/KeyHandoverContext";
import KeyHandoverLocationSelectionStep from "../steps/KeyHandoverLocationSelectionStep";
import { KeyHandoverPageLayout } from "../components/KeyHandoverPageLayout";
import { KeyHandoverContentWrapper } from "../components/KeyHandoverContentWrapper";

/**
 * LocationSelectionContent handles location hydration and URL synchronization
 */
function LocationSelectionContent() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const selectedLocation = useSelector(
        (state: RootState) => state.bookingCreation.selectedLocation
    );
    const locs = useKeyHandoverLocations();
    const lastWrittenLocationIdRef = useRef<string | null>(null);

    // Hydrate selection from query once locations are available
    useEffect(() => {
        if (!locs || locs.length === 0) return;
        const locId = searchParams.get("locationId");
        if (!locId) return;
        if (!selectedLocation || selectedLocation.id !== locId) {
            const found = locs.find((l) => l.id === locId) || null;
            dispatch(bookingStepChanged({ step: 0, nextEnabled: Boolean(found) }));
            if (found) {
                dispatch(setSelectedLocationAction(found));
                dispatch(setSubscriptionSelectedLocation(found)); // Also store in subscription slice
                dispatch(setHasSelectedLocationAction(true));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locs]);

    // Keep query string in sync with selection without causing flicker loops
    useEffect(() => {
        const newId = selectedLocation?.id ?? null;
        if (!newId) return;
        if (lastWrittenLocationIdRef.current === newId) return;

        const current = new URLSearchParams(location.search);
        const currentId = current.get("locationId");
        if (currentId === newId) {
            lastWrittenLocationIdRef.current = newId;
            return;
        }
        current.set("locationId", newId);
        lastWrittenLocationIdRef.current = newId;
        navigate(
            {
                pathname: location.pathname,
                search: current.toString() ? `?${current.toString()}` : "",
            },
            { replace: true }
        );
    }, [selectedLocation?.id, navigate, location.pathname, location.search]);

    return <KeyHandoverLocationSelectionStep />;
}

/**
 * Standalone page for selecting a key handover location.
 * URL: /key-handover/location-selection
 */
const KeyHandoverLocationSelectionPage: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const selectedLocation = useSelector(
        (state: RootState) => state.bookingCreation.selectedLocation
    );

    // On mount, initialize booking context
    useEffect(() => {
        dispatch(setUseCase({ useCase: "keys" }));
        dispatch(bookingStepChanged({ step: 0, nextEnabled: !!selectedLocation }));
        dispatch(
            bookingFlowChanged({
                bookingFlow: ["locationSelection", "assetGroupSelection", "orderData"],
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // Bottom actions for location selection
    const bottomActions = (
        <div className="pt-4">
            <KeyHandoverNavigateNextAction
                to={routes.keyHandoverPaymentModelPath}
                enabled={Boolean(selectedLocation?.id)}
                bounceKey={selectedLocation?.id ?? null}
            />
        </div>
    );

    return (
        <KeyHandoverPageLayout
            headerText={t("book_location.select_location")}
            // First page - no back button (backTo omitted)
            bottomActions={bottomActions}
        >
            <KeyHandoverContentWrapper>
                <LocationSelectionContent />
            </KeyHandoverContentWrapper>
        </KeyHandoverPageLayout>
    );
};

export default KeyHandoverLocationSelectionPage;
